import {
  type LineInfo,
  rhymeStart,
  type WordInfo,
} from "@/lib/tool-kit/poetry/pronounce";

/**
 * Rhyme schemes from line endings, by pronunciation.
 *
 * **Definitions used here**, so a reader can check the tool against a textbook:
 *
 * - The *rhyming part* of a word runs from its last stressed vowel to its end, taken from
 *   the CMU Pronouncing Dictionary ("flower" → AW ER, "day" → EY).
 * - **Perfect rhyme** — two different words with the same rhyming part, so the stressed
 *   vowel and every sound after it match ("day" / "play", "flying" / "dying").
 * - **Identical** — the same word again, as in a refrain; counted as a rhyme link.
 * - **Slant rhyme** — not perfect, but sharing either the stressed vowel (*assonance*:
 *   "time" / "mind") or the closing consonants after different vowels (*consonance*:
 *   "bone" / "gone", "heart" / "hurt").
 * - **Eye rhyme** — spelled alike at the end but not rhyming by sound ("love" / "move").
 *   Reported as a note, never counted as a rhyme.
 *
 * A word the dictionary lacks is compared by the spelling of its ending, and flagged as a
 * guess.
 */

export type Relation = "identical" | "perfect" | "slant" | "none";
export type SlantKind = "assonance" | "consonance" | "final-vowel";

export interface EndWord {
  word: WordInfo;
  /** Digit-free rhyming parts; more than one when verse stresses the word two ways. */
  parts: string[][];
  /** The rhyming part with stress digits, for display. Null for a guessed word. */
  tail: string[] | null;
  /** The spelling of the ending, used for guesses and for eye rhyme. */
  spelling: string;
  /** Syllables in the rhyming part: 1 masculine, 2 feminine, 3 or more triple. */
  tailSyllables: number;
}

export interface Link {
  relation: Relation;
  kind: SlantKind | null;
  /** True when either word was compared by spelling. */
  guess: boolean;
  /** True when spelling alone would suggest a rhyme the sounds do not support. */
  eye: boolean;
}

const RE_STRESS_DIGIT = /\d$/;
const RE_SPELLING_TAIL = /[aeiouy]+[^aeiouy]+e?$|[aeiouy]+$/;
const MIN_EYE_LETTERS = 3;
const MIN_GUESS_LETTERS = 2;

function stripDigit(phone: string): string {
  return phone.replace(RE_STRESS_DIGIT, "");
}

function isVowel(phone: string): boolean {
  return RE_STRESS_DIGIT.test(phone);
}

/**
 * Words whose last syllable is reduced by the dictionary but takes a full vowel in older
 * verse: Shakespeare rhymes "temperate" with "date".
 */
function verseStressTail(
  key: string,
  phones: readonly string[],
  start: number
): string[] | null {
  const n = phones.length;
  if (
    key.endsWith("ate") &&
    phones[n - 1] === "T" &&
    (phones[n - 2] === "AH0" || phones[n - 2] === "IH0") &&
    start < n - 2
  ) {
    return ["EY", "T"];
  }
  return null;
}

function spellingOf(key: string): string {
  return RE_SPELLING_TAIL.exec(key)?.[0] ?? key;
}

/** The last word of a line, described for rhyming; null when the line has no word. */
export function endWord(line: LineInfo): EndWord | null {
  const word = line.words.at(-1);
  if (!word) {
    return null;
  }
  const spelling = spellingOf(word.key);
  if (!word.phones) {
    return { word, parts: [], tail: null, spelling, tailSyllables: 0 };
  }
  const start = rhymeStart(word.phones);
  const tail = word.phones.slice(start);
  const parts = [tail.map(stripDigit)];
  const shifted = verseStressTail(word.key, word.phones, start);
  if (shifted) {
    parts.push(shifted);
  }
  return {
    word,
    parts,
    tail,
    spelling,
    tailSyllables: tail.filter(isVowel).length,
  };
}

function sameParts(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((phone, i) => phone === b[i]);
}

const VOWELS: ReadonlySet<string> = new Set([
  "AA",
  "AE",
  "AH",
  "AO",
  "AW",
  "AY",
  "EH",
  "ER",
  "EY",
  "IH",
  "IY",
  "OW",
  "OY",
  "UH",
  "UW",
]);

/** The consonants after the last vowel of a digit-free part. */
function closing(part: readonly string[]): string[] {
  let lastVowel = -1;
  for (const [i, phone] of part.entries()) {
    if (VOWELS.has(phone)) {
      lastVowel = i;
    }
  }
  return part.slice(lastVowel + 1);
}

function slantKind(
  a: readonly string[],
  b: readonly string[]
): SlantKind | null {
  if (a[0] === b[0]) {
    return "assonance";
  }
  const closeA = closing(a);
  const closeB = closing(b);
  if (closeA.length > 0 && sameParts(closeA, closeB)) {
    return "consonance";
  }
  const lastA = a.at(-1) ?? "";
  if (VOWELS.has(lastA) && lastA === b.at(-1)) {
    // "me" / "Immortality": the words end on the same vowel, one of them unstressed.
    return "final-vowel";
  }
  return null;
}

function eyeRhyme(a: EndWord, b: EndWord): boolean {
  return (
    a.spelling === b.spelling &&
    a.spelling.length >= MIN_EYE_LETTERS &&
    a.word.key !== b.word.key
  );
}

/** How two line endings relate. */
export function relate(a: EndWord, b: EndWord): Link {
  const none: Link = { relation: "none", kind: null, guess: false, eye: false };
  if (a.word.key === b.word.key) {
    return { ...none, relation: "identical" };
  }
  if (a.parts.length === 0 || b.parts.length === 0) {
    // At least one word is only estimated: compare how the endings are spelled.
    const same =
      a.spelling === b.spelling &&
      a.spelling.length >= MIN_GUESS_LETTERS &&
      a.word.key !== b.word.key;
    return { ...none, relation: same ? "perfect" : "none", guess: true };
  }
  for (const partA of a.parts) {
    for (const partB of b.parts) {
      if (sameParts(partA, partB)) {
        return { ...none, relation: "perfect" };
      }
    }
  }
  const kind = slantKind(a.parts[0], b.parts[0]);
  const eye = eyeRhyme(a, b);
  if (kind) {
    return { relation: "slant", kind, guess: false, eye };
  }
  return { ...none, eye };
}

/* ----------------------------------------------------------------- scheme */

export interface RhymeOptions {
  /** Count slant rhymes as rhymes when lettering. */
  slant: boolean;
  /** Count the same end word again as a rhyme (a refrain). */
  identical: boolean;
}

export interface SchemeLine {
  line: LineInfo;
  /** Index of the stanza (blank-line separated), from 0. */
  stanza: number;
  end: EndWord | null;
  /** The scheme letter, or null for a line with no ending word. */
  letter: string | null;
  /** The earlier line this one was matched with, if any. */
  match: { line: SchemeLine; link: Link } | null;
}

export interface NearRhyme {
  first: SchemeLine;
  second: SchemeLine;
  link: Link;
}

export type FormId =
  | "couplets"
  | "alternate"
  | "enclosed"
  | "ballad"
  | "monorhyme"
  | "limerick"
  | "shakespearean"
  | "petrarchan"
  | "spenserianSonnet"
  | "rhymeRoyal"
  | "ottavaRima"
  | "spenserianStanza"
  | "terzaRima"
  | "villanelle"
  | "tercets";

export interface RhymeAnalysis {
  lines: SchemeLine[];
  /** Letters per stanza, e.g. ["ABAB", "CDCD", "EFEF", "GG"]. */
  stanzas: string[];
  /** The whole scheme as one string. */
  scheme: string;
  form: FormId | null;
  /** Slant pairs found but not counted, when slant rhyme is off. */
  nearRhymes: NearRhyme[];
  /** Number of distinct rhyme sounds. */
  sounds: number;
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export function letterFor(index: number): string {
  const round = Math.floor(index / ALPHABET.length);
  const letter = ALPHABET[index % ALPHABET.length];
  return round === 0 ? letter : `${letter}${round + 1}`;
}

const RANK: Record<Relation, number> = {
  identical: 3,
  perfect: 2,
  slant: 1,
  none: 0,
};

function counts(link: Link, options: RhymeOptions): boolean {
  if (link.relation === "identical") {
    return options.identical;
  }
  if (link.relation === "slant") {
    return options.slant;
  }
  return link.relation === "perfect";
}

interface Group {
  letter: string;
  members: SchemeLine[];
}

function bestMatch(
  entry: SchemeLine,
  groups: readonly Group[],
  options: RhymeOptions
): { group: Group; member: SchemeLine; link: Link } | null {
  let best: { group: Group; member: SchemeLine; link: Link } | null = null;
  for (const group of groups) {
    for (const member of group.members) {
      if (!(entry.end && member.end)) {
        continue;
      }
      const link = relate(member.end, entry.end);
      if (!counts(link, options)) {
        continue;
      }
      if (!best || RANK[link.relation] > RANK[best.link.relation]) {
        best = { group, member, link };
      }
    }
  }
  return best;
}

function collectNear(entries: readonly SchemeLine[]): NearRhyme[] {
  const near: NearRhyme[] = [];
  for (const [i, second] of entries.entries()) {
    for (const first of entries.slice(0, i)) {
      if (!(first.end && second.end)) {
        continue;
      }
      const link = relate(first.end, second.end);
      if (link.relation === "slant" && first.letter !== second.letter) {
        near.push({ first, second, link });
      }
    }
  }
  return near.slice(0, 12);
}

/** Letter a poem's line endings. `lines` includes blank lines, which separate stanzas. */
export function analyzeRhyme(
  lines: readonly LineInfo[],
  options: RhymeOptions
): RhymeAnalysis {
  const entries: SchemeLine[] = [];
  const groups: Group[] = [];
  let stanza = 0;
  let sawLine = false;
  for (const line of lines) {
    if (line.text.trim().length === 0) {
      if (sawLine) {
        stanza += 1;
        sawLine = false;
      }
      continue;
    }
    sawLine = true;
    const entry: SchemeLine = {
      line,
      stanza,
      end: endWord(line),
      letter: null,
      match: null,
    };
    const found = entry.end ? bestMatch(entry, groups, options) : null;
    if (found) {
      entry.letter = found.group.letter;
      entry.match = { line: found.member, link: found.link };
      found.group.members.push(entry);
    } else if (entry.end) {
      const group = { letter: letterFor(groups.length), members: [entry] };
      groups.push(group);
      entry.letter = group.letter;
    }
    entries.push(entry);
  }
  const stanzas = lettersByStanza(entries);
  const scheme = stanzas.join("");
  return {
    lines: entries,
    stanzas,
    scheme,
    form: detectForm(stanzas, entries),
    nearRhymes: options.slant ? [] : collectNear(entries),
    sounds: groups.length,
  };
}

function lettersByStanza(entries: readonly SchemeLine[]): string[] {
  const out: string[] = [];
  for (const entry of entries) {
    out[entry.stanza] = (out[entry.stanza] ?? "") + (entry.letter ?? "-");
  }
  return out.filter((s) => s !== undefined);
}

/* ------------------------------------------------------------- form names */

/** Letters renamed in order of first appearance, so ["CDC"] and ["ABA"] compare equal. */
export function canonical(scheme: string): string {
  const seen = new Map<string, string>();
  let out = "";
  for (const ch of scheme) {
    if (!seen.has(ch)) {
      seen.set(ch, ALPHABET[seen.size] ?? ch);
    }
    out += seen.get(ch);
  }
  return out;
}

const RE_PETRARCHAN = /^ABBAABBA(?:CDECDE|CDCDCD|CDCCDC|CDEDCE|CDDCDD|CDEEDC)$/;
const RE_SHAKESPEAREAN = /^ABABCDCDEFEFGG$/;
const RE_SPENSERIAN_SONNET = /^ABABBCBCCDCDEE$/;

const WHOLE: [RegExp | string, FormId][] = [
  [RE_SHAKESPEAREAN, "shakespearean"],
  [RE_PETRARCHAN, "petrarchan"],
  [RE_SPENSERIAN_SONNET, "spenserianSonnet"],
  ["ABAABAABAABAABAABAA", "villanelle"],
  ["AABBA", "limerick"],
  ["ABABBCC", "rhymeRoyal"],
  ["ABABABCC", "ottavaRima"],
  ["ABABBCBCC", "spenserianStanza"],
];

function matches(pattern: RegExp | string, scheme: string): boolean {
  return typeof pattern === "string"
    ? pattern === scheme
    : pattern.test(scheme);
}

function isTerzaRima(scheme: string): boolean {
  if (scheme.length < 6) {
    return false;
  }
  // ABA BCB CDC … with a closing couplet or single line allowed.
  const body = scheme.length % 3 === 0 ? scheme : scheme.slice(0, -1);
  for (let i = 0; i + 2 < body.length; i += 3) {
    const [x, y, z] = [body[i], body[i + 1], body[i + 2]];
    const next = body[i + 3];
    if (x !== z || x === y) {
      return false;
    }
    if (next !== undefined && next !== y) {
      return false;
    }
  }
  return true;
}

const STANZA_FORMS: Record<string, FormId> = {
  AA: "couplets",
  AABB: "couplets",
  AAA: "tercets",
  ABAB: "alternate",
  ABBA: "enclosed",
  ABCB: "ballad",
  AAAA: "monorhyme",
};

const RE_PAIR = /../g;

/** AABBCC… — rhymed pairs running on without stanza breaks. */
function isRunOnCouplets(scheme: string): boolean {
  if (scheme.length < 4 || scheme.length % 2 !== 0) {
    return false;
  }
  const pairs = scheme.match(RE_PAIR) ?? [];
  return pairs.every((pair) => pair[0] === pair[1]);
}

function stanzaForm(stanzas: readonly string[]): FormId | null {
  const shapes = stanzas.map((stanza) => canonical(stanza));
  if (shapes.length === 0 || shapes.some((shape) => shape !== shapes[0])) {
    return null;
  }
  return STANZA_FORMS[shapes[0]] ?? null;
}

function detectForm(
  stanzas: readonly string[],
  entries: readonly SchemeLine[]
): FormId | null {
  if (entries.length < 2 || entries.some((entry) => entry.letter === null)) {
    return null;
  }
  const whole = canonical(stanzas.join(""));
  for (const [pattern, id] of WHOLE) {
    if (matches(pattern, whole)) {
      return id;
    }
  }
  if (isTerzaRima(whole)) {
    return "terzaRima";
  }
  if (stanzas.length === 1) {
    return STANZA_FORMS[whole] ?? (isRunOnCouplets(whole) ? "couplets" : null);
  }
  return stanzaForm(stanzas) ?? (isRunOnCouplets(whole) ? "couplets" : null);
}
